/**
 * Shared presentational atoms for event content bodies. Each per-kind
 * component composes these instead of re-implementing rows, so the CSS
 * contract (.mc-event-time/-title/-meta/-flags, strips) stays in one place.
 */
import React from "react";
import { MCEvent } from "../model/types";
import { getContrastTextColor } from "../../../themes/colorUtils";

export interface MCEventContentProps {
  event: MCEvent;
  /** Very short events collapse to a single line (CSS-assisted) */
  isCompact: boolean;
}

/** Visit type → compact code (mirrors helpers/visitTypeMapping). */
const VISIT_TYPE_CODE: Record<string, string> = {
  haus: "HB",
  praxis: "PX",
  heim: "HM",
};

export const visitCodeOf = (event: MCEvent): string | undefined =>
  event.visitType
    ? VISIT_TYPE_CODE[event.visitType.toLowerCase()] ??
      event.visitType.slice(0, 2).toUpperCase()
    : undefined;

/** "HH:mm–HH:mm · 45′ ∞" first line. */
export const TimeRange: React.FC<{ event: MCEvent }> = ({ event }) => {
  const durationMin = event.endMin - event.startMin;
  const showInfinity = event.isContinuous || event.rxPerpetual;
  return (
    <span className="mc-event-time">
      {event.startLabel}–{event.endLabel}
      {durationMin >= 30 ? ` · ${durationMin}′` : ""}
      {showInfinity && <span className="mc-flag-infinity"> ∞</span>}
    </span>
  );
};

/** Status icon row; kinds opt in — icons only render when their flag is set. */
export const EventFlags: React.FC<{ event: MCEvent }> = ({ event }) => (
  <span className="mc-event-flags">
    {event.rxInCart && (
      <span className="mc-flag-cart" title="Rezept im Warenkorb">🛒</span>
    )}
    {event.rxMissing && (
      <span className="mc-flag-rx" title="Verordnung ohne Datum">Rx!?</span>
    )}
    {event.containsHeatTreatment && <span title="Warmpackung inklusive">♨</span>}
    {event.isOutsideWorkingHours && (
      <span className="mc-flag-warning" title="Außerhalb der Arbeitszeit">⚠</span>
    )}
    {event.hasNotes && <span className="mc-flag-notes" title="Terminhinweise" />}
    {event.attended && <span className="mc-flag-attended" title="Wahrgenommen">✓</span>}
    {event.attended && !event.wasDocumented && (
      <span className="mc-flag-doc-missing" title="Dokumentation fehlt">!</span>
    )}
  </span>
);

/** Therapy abbr, treatment index, cancel reason and flags on one row. */
export const MetaRow: React.FC<{ event: MCEvent }> = ({ event }) => (
  <span className="mc-event-meta">
    {event.therapyAbbr && <span className="mc-event-therapy">{event.therapyAbbr}</span>}
    {event.treatmentIndex && <span className="mc-event-time">#{event.treatmentIndex}</span>}
    {event.isCancelled && event.cancelReason && (
      <span className="mc-event-reason" title={event.cancelReason}>
        *{event.cancelReason}
      </span>
    )}
    <EventFlags event={event} />
  </span>
);

export const stripTitleOf = (event: MCEvent): string => {
  const hasFee = Boolean(event.visitFeeAbbr && event.visitFeeAbbr !== "keine");
  return [
    event.visitType,
    event.sublocalityName || event.sublocalityAbbr,
    hasFee ? `Gebühr: ${event.visitFeeAbbr}` : undefined,
  ]
    .filter(Boolean)
    .join(" · ");
};

/**
 * Sublocality visualization. `full` renders the 24px right-hand strip
 * (visit code, € fee marker, sublocality abbr on the sublocality color);
 * otherwise a thin color bar when the event has a sublocality color at all.
 * The frame's `has-subloc` class uses the same predicate, so padding and
 * strip always agree.
 */
export const wantsFullStrip = (event: MCEvent, isCompact: boolean): boolean =>
  event.kind === "appointment" &&
  !isCompact &&
  Boolean(visitCodeOf(event) || event.sublocalityAbbr);

export const SublocalityStrip: React.FC<{
  event: MCEvent;
  full: boolean;
}> = ({ event, full }) => {
  if (full) {
    const visitCode = visitCodeOf(event);
    const hasFee = Boolean(event.visitFeeAbbr && event.visitFeeAbbr !== "keine");
    const bg = event.sublocalityColor || "#d3d7db";
    return (
      <span
        className="mc-event-subloc-strip"
        style={{ background: bg, color: getContrastTextColor(bg) }}
        title={stripTitleOf(event)}
      >
        {visitCode && <span className="mc-subloc-vt">{visitCode}</span>}
        {hasFee && <span className="mc-subloc-fee">€</span>}
        {event.sublocalityAbbr && (
          <span
            className={`mc-subloc-abbr${event.sublocalityAbbr.length > 4 ? " is-long" : ""}`}
          >
            {event.sublocalityAbbr}
          </span>
        )}
      </span>
    );
  }
  if (!event.sublocalityColor) return null;
  return (
    <span
      className="mc-event-subloc"
      style={{ background: event.sublocalityColor }}
      title={stripTitleOf(event) || event.sublocalityAbbr}
    />
  );
};
